import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Clock, Search, ArrowRight, KeyRound, TriangleAlert, Home, CalendarDays } from 'lucide-react';
import { Badge, Button, Money } from '../../components/ui';
import { FormError } from '../../components/forms/common';
import { ApplicationFields } from '../../components/lettings/ApplicationFields';
import { ReadOnlyForm } from '../../components/lettings/Drawer';
import { fromApplication, refillErrors, splitName, toPayload, type ApplicationState } from '../../lib/applicationForm';
import { PageLoader } from '../../components/BrandLoader';
import { useAuth } from '../../stores/auth';
import { formatDate } from '../../lib/viz';
import { myBookingApi, bookingPagePath, nextLine, bedsLabel, viewingStatus, viewingWhen, type MyBooking, type MyViewing } from '../../lib/myBooking';
import { TPageHead, TCard, THero, TLinkButton, TFacts, TEmpty } from '../../components/tenant/kit';
import { formatPhone } from '../../lib/phone';

/**
 * An applicant's home on the website (someone who signed up in the app to find a home — the Applicant role,
 * `applicantPortal.read`). It mirrors the app's home for a non-resident (mobile/src/app/(tabs)/index.tsx): the booking
 * under way — where it has got to, the five steps and the way back into it (/apply/<token>, the booking's own page) —
 * or, with no booking, the way to the homes (/rent) and how booking works. Their account details sit underneath.
 * Viewing first (docs/plans/viewing-first-onboarding.md, phase 4): "Your viewings" sits above it — each viewing's flat, day
 * and time, status and host — and once Lotus Living marks one completed, "Apply for this flat" opens the application
 * (pages/applicant/ApplyFromViewing.tsx, /app/my-booking/apply/:leadId).
 * It wears the portal's look (TenantShell, components/tenant/kit). Lotus Living's "Ask to refill" shows on the booking with
 * Update my application (the one form, mode apply, only what was asked for required → POST /app/my-booking/form, which
 * clears the request), and My application shows what they sent, read-only (docs/plans/lettings-one-form.md, phase 4).
 */

/**
 * How it works, viewing first (docs/plans/viewing-first-onboarding.md, 2 Nov 2026): book a viewing → apply → referencing →
 * sign → move in. The last three are the booking's own steps (referencing takes the holding deposit with it).
 */
const STEPS: Array<{ title: string; body: string; keys: Array<MyBooking['steps'][number]['key']> | 'viewing' | 'apply' }> = [
  { title: 'Book a viewing', body: 'Pick a free time on a home you like.', keys: 'viewing' },
  { title: 'Apply', body: 'After the viewing — your details are already filled in.', keys: 'apply' },
  { title: 'Referencing', body: 'The holding deposit, your ID, right to rent and income.', keys: ['holding', 'referencing'] },
  { title: 'Sign the agreement', body: 'Read it and sign it online.', keys: ['sign'] },
  { title: 'Move in', body: 'First rent and deposit, then the keys are yours.', keys: ['money'] },
];

export default function MyBookingPage() {
  const user = useAuth((s) => s.user);
  const booking = useQuery({ queryKey: ['my-booking'], queryFn: myBookingApi.booking, refetchInterval: (q) => (q.state.data?.status === 'applying' ? 30_000 : false) });
  const profile = useQuery({ queryKey: ['my-profile'], queryFn: myBookingApi.profile });
  const viewings = useQuery({ queryKey: ['my-viewings'], queryFn: myBookingApi.viewings });
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  if (booking.isLoading || viewings.isLoading) return <PageLoader />;
  const b = booking.data ?? null;
  const vs = viewings.data ?? [];
  const ready = vs.find((v) => v.canApply) ?? null;
  // Each step done or not: the viewing and the application from what there is, the rest from the booking's own steps.
  const stepDone = STEPS.map((st) => (st.keys === 'viewing' ? vs.some((v) => v.status === 'COMPLETED') || !!b
    : st.keys === 'apply' ? !!b
    : !!b && st.keys.every((k) => !!b.steps.find((x) => x.key === k)?.done)));
  const doneCount = stepDone.filter(Boolean).length;
  const first = user?.firstName || (b && !b.applicant.name.includes('@') ? b.applicant.name.split(/\s+/)[0] : null);

  return (
    <div className="space-y-4 sm:space-y-5">
      <TPageHead overline={b ? 'Welcome back' : 'Find a home'} title={first ? `Hello, ${first}` : 'Welcome to Lotus Living'}
        sub={b ? 'Where your booking has got to, and the way back into it. Each step is ticked as it is done, and Lotus Living sees it straight away.' : vs.length ? 'Your viewings, and the way to apply once you have seen the flat.' : undefined} />

      {vs.length > 0 && <ViewingsCard viewings={vs} />}

      <div className="grid items-start gap-4 lg:grid-cols-[1.35fr_1fr] lg:gap-5">
        {booking.error ? <TEmpty icon={TriangleAlert} title="Your booking could not be opened" body={(booking.error as Error).message} />
          : b ? <BookingHero b={b} saved={saved} onUpdate={() => setEditing(true)} />
          : ready ? (
            <THero overline="Your next step" title={`Apply for Flat ${ready.unit.reference}`}
              meta={`Your viewing at ${ready.property.name} is done. Your details from it are already filled in — four short steps, no fees.`}>
              <div className="flex flex-wrap gap-2.5">
                <TLinkButton to={`/app/my-booking/apply/${ready.leadId}`}>Apply for this flat <ArrowRight className="h-[18px] w-[18px]" /></TLinkButton>
                <TLinkButton to="/rent" variant="soft" icon={Search}>Browse more homes</TLinkButton>
              </div>
            </THero>
          ) : vs.length ? (
            <THero overline="Your next step" title="Apply after your viewing"
              meta="Once Lotus Living marks your viewing completed, “Apply for this flat” opens on it — your details are already filled in.">
              <TLinkButton to="/rent" variant="soft" icon={Search} className="w-full sm:w-auto sm:min-w-[220px]">Browse more homes</TLinkButton>
            </THero>
          ) : (
            <THero overline="No booking yet" title="Book a viewing on a home to get started"
              meta="Pick a home, book a free time to see it, then apply from here — all online.">
              <TLinkButton to="/rent" icon={Search} className="w-full sm:w-auto sm:min-w-[220px]">Find a home</TLinkButton>
            </THero>
          )}

        <TCard i={1} title="How it works" sub={doneCount ? `${doneCount} of ${STEPS.length} done` : 'Five steps, all online'} flush>
          <ol className="px-5 pb-3">
            {STEPS.map((st, i) => {
              const done = stepDone[i]!;
              const step = b && Array.isArray(st.keys) ? b.steps.find((x) => !x.done && (st.keys as string[]).includes(x.key)) ?? null : null;
              const review = !!b && b.reviewStatus !== 'APPROVED' && b.reviewStatus !== 'REJECTED' && Array.isArray(st.keys) && !done;
              const note = !b || done ? null : review ? 'Opens once Lotus Living approves your application' : step?.lockedBy ?? step?.pending ?? null;
              const last = i === STEPS.length - 1;
              return (
                <li key={st.title} className="relative flex gap-3.5 pb-4">
                  {!last && <span aria-hidden className={`absolute left-[15px] top-9 h-[calc(100%-28px)] w-px ${done ? 'bg-bronze-300' : 'bg-ink-200'}`} />}
                  <span className={`relative grid h-8 w-8 shrink-0 place-items-center rounded-full text-[13px] font-semibold ${done ? 'text-white' : 'bg-ink-100 text-ink-600'}`} style={done ? { background: 'var(--t-foil)' } : undefined}>
                    {done ? <Check className="h-4 w-4" aria-label="Done" /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1 pt-1">
                    <span className="block text-[15px] font-semibold text-ink-900">{st.title}</span>
                    <span className="block text-[13px] text-ink-500">{st.body}</span>
                    {note && <span className="mt-1 flex items-center gap-1.5 text-[12.5px] text-ink-500">{step?.lockedBy || review ? <Lock className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}{note}</span>}
                  </span>
                </li>
              );
            })}
          </ol>
        </TCard>
      </div>

      {b && editing && b.refill && <RefillCard b={b} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); setSaved(true); }} />}

      {b?.application && (
        <TCard i={2} title="My application" sub="What you sent Lotus Living">
          <ReadOnlyForm form={b.application} />
        </TCard>
      )}

      <TCard i={3} title="Your details" action={<TLinkButton to="/app/change-password" variant="soft" small icon={KeyRound}>Change password</TLinkButton>}>
        <TFacts items={[
          { k: 'Name', v: [profile.data?.account.firstName ?? user?.firstName, profile.data?.account.lastName ?? user?.lastName].filter(Boolean).join(' ') || 'Asked for when you book' },
          { k: 'Email', v: <span className="break-all">{profile.data?.account.email ?? user?.email ?? '—'}</span> },
          { k: 'Mobile', v: formatPhone(profile.data?.account.phone) || '—' },
          { k: 'Budget check', v: screeningLabel(profile.data?.screening) },
        ]} />
      </TCard>
    </div>
  );
}

/** Your viewings: one row per viewing — the flat, the day and time, the status, the host — and Apply once it is completed. */
function ViewingsCard({ viewings }: { viewings: MyViewing[] }) {
  return (
    <TCard i={0} title="Your viewings" sub="Lotus Living marks a viewing completed after it — then you can apply for the flat." flush>
      <ul className="divide-y divide-ink-100 px-5 pb-2">
        {viewings.map((v) => {
          const [label, tone] = viewingStatus(v);
          return (
            <li key={v.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3.5">
                <span className="grid h-16 w-20 shrink-0 place-items-center overflow-hidden rounded-xl bg-bronze-50 text-bronze-600">
                  {v.listing.coverThumbUrl ? <img src={v.listing.coverThumbUrl} alt="" className="h-full w-full object-cover" /> : <Home className="h-5 w-5" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold text-ink-900">Flat {v.unit.reference}, {v.property.name}</span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-500">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{viewingWhen(v.scheduledAt)}</span>
                    {v.host && <span>· with {v.host.firstName}</span>}
                    <StatusPill label={label} tone={tone} />
                  </span>
                  {v.status === 'COMPLETED' && v.applyBlockedBy && <span className="mt-1 block text-[12.5px] text-ink-500">{v.applyBlockedBy}</span>}
                </span>
              </div>
              {v.canApply && <TLinkButton to={`/app/my-booking/apply/${v.leadId}`} className="w-full sm:w-auto">Apply for this flat <ArrowRight className="h-[18px] w-[18px]" /></TLinkButton>}
            </li>
          );
        })}
      </ul>
    </TCard>
  );
}

const PILL: Record<ReturnType<typeof viewingStatus>[1], string> = {
  good: 'bg-[#E4ECE0] text-[#3F6340]', warning: 'bg-bronze-50 text-bronze-700', critical: 'bg-[#F0DED9] text-[#8A3F33]', neutral: 'bg-ink-100 text-ink-600',
};
function StatusPill({ label, tone }: { label: string; tone: ReturnType<typeof viewingStatus>[1] }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${PILL[tone]}`}>{label}</span>;
}

function screeningLabel(s: { status: string; maxRentMinor: number | null; currency: string } | undefined): React.ReactNode {
  if (!s || s.status === 'none') return <span className="font-normal text-ink-500">Not done yet — it comes with your first booking</span>;
  if (s.maxRentMinor) return <>Up to <Money minor={s.maxRentMinor} currency={s.currency} /> a month</>;
  return s.status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

/** The booking under way, as the hero card: the flat, its status, the progress and the way back in. */
function BookingHero({ b, saved, onUpdate }: { b: MyBooking; saved: boolean; onUpdate: () => void }) {
  const rejected = b.reviewStatus === 'REJECTED';
  const status = rejected ? ['Not successful', 'critical'] as const
    : b.status === 'moved_in' ? ['Moved in', 'good'] as const
    : b.status === 'with_lotus' ? ['Getting your home ready', 'good'] as const
    : b.reviewStatus === 'APPROVED' ? ['Approved', 'good'] as const
    : ['Under review', 'warning'] as const;
  const pct = b.total ? Math.round(((b.done + 1) / (b.total + 1)) * 100) : 0;
  return (
    <THero overline={<span className="inline-flex flex-wrap items-center gap-2"><Home className="h-3.5 w-3.5" />Your booking</span>} title={`Flat ${b.flat}, ${b.property}`}
      meta={<>
        <span className="mb-3 block"><Badge value={status[0]} tone={status[1]} /></span>
        <span className="grid grid-cols-2 gap-x-4 gap-y-2 text-[14px] sm:grid-cols-4">
          <span><span className="block text-[12px] text-ink-500">Rent</span><span className="font-semibold text-ink-900"><Money minor={b.rentMinor} currency={b.currency} /> a month</span></span>
          <span><span className="block text-[12px] text-ink-500">Type</span><span className="font-semibold text-ink-900">{bedsLabel(b.bedrooms, b.unitType)}{b.areaSqft ? ` · ${b.areaSqft} sq ft` : ''}</span></span>
          <span><span className="block text-[12px] text-ink-500">Moving in</span><span className="font-semibold text-ink-900">{b.startDate ? formatDate(b.startDate) : 'To be agreed'}</span></span>
          <span><span className="block text-[12px] text-ink-500">Term</span><span className="font-semibold text-ink-900">{b.term}</span></span>
        </span>
        {!rejected && (
          <span className="mt-5 block">
            <span className="flex items-baseline justify-between text-[13px]"><span className="font-semibold text-ink-900">{b.done + 1} of {b.total + 1} steps done</span><span className="text-ink-500">{pct}%</span></span>
            <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-white/80"><span className="block h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--t-foil)' }} /></span>
          </span>
        )}
        {b.refill && !rejected && (
          <span className="mt-4 block rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-[13.5px] leading-relaxed text-ink-700">
            <span className="block font-semibold text-ink-900">Lotus Living asked you to update: {b.refill.labels.join(', ').toLowerCase()}</span>
            {b.refill.note && <span className="mt-1 block">{b.refill.note}</span>}
            <button type="button" onClick={onUpdate} className="t-btn t-btn-foil t-btn-sm mt-3">Update my application</button>
          </span>
        )}
        {saved && !b.refill && <span className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-[13.5px] font-semibold text-emerald-800"><Check className="h-4 w-4" />Thanks — Lotus Living has your update</span>}
        <span className="mt-4 block text-[14px] leading-relaxed text-ink-600">{nextLine(b)}{rejected && b.rejectionReason ? ` The reason: ${b.rejectionReason}` : ''}</span>
      </>}>
      <div className="flex flex-wrap gap-2.5">
        {rejected ? <TLinkButton to="/rent" icon={Search}>Find another home</TLinkButton>
          : <TLinkButton to={bookingPagePath(b)}>{b.status === 'applying' ? 'Continue your booking' : 'Open your booking'} <ArrowRight className="h-[18px] w-[18px]" /></TLinkButton>}
        {!rejected && b.status === 'applying' && <TLinkButton to="/rent" variant="soft" icon={Search}>Browse homes</TLinkButton>}
      </div>
    </THero>
  );
}

/**
 * Update my application: the whole form again, pre-filled with what was sent; only what Lotus Living asked for (and the
 * name, a way to reach them, the move-in date) is required. The consents given with the application stand unless they were
 * asked for again. Saving clears the request.
 */
function RefillCard({ b, onClose, onSaved }: { b: MyBooking; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const asked = b.refill?.fields ?? [];
  const [s, setS] = useState<ApplicationState>(() => fromApplication(b.application, { ...splitName(b.applicant.name), email: b.applicant.email ?? '', mobile: b.applicant.phone ?? '' }));
  const [tried, setTried] = useState(false);
  const errs = refillErrors(s, b.rentMinor, asked);
  const save = useMutation({
    mutationFn: () => {
      const form = toPayload(s, { staff: true });
      const was = b.application?.consent;
      if (was && typeof was === 'object' && !asked.includes('consent')) Object.assign(form, { consent: was });
      return myBookingApi.saveForm(form);
    },
    onSuccess: (d) => { qc.setQueryData(['my-booking'], d); onSaved(); },
  });
  return (
    <TCard i={2} title="Update my application" sub={`Lotus Living asked you to update: ${b.refill?.labels.join(', ').toLowerCase()}. Fields marked * are needed.`}>
      <ApplicationFields self s={s} set={(p) => setS((cur) => ({ ...cur, ...p }))} advert={b.rentMinor} asked={asked} showErrors={tried} />
      <div className="mt-4 space-y-3">
        <FormError error={save.error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setTried(true); if (!Object.keys(errs).length) save.mutate(); }} disabled={save.isPending}>{save.isPending ? 'Sending…' : 'Send my update'}</Button>
        </div>
      </div>
    </TCard>
  );
}
